import { useState } from "react"

import {
    Combobox,
    ComboboxButton,
    ComboboxInput,
    ComboboxOption,
    ComboboxOptions
} from "@headlessui/react"
import {
    CheckIcon,
    ChevronUpDownIcon
} from "@heroicons/react/20/solid"
import { XMarkIcon } from "@heroicons/react/24/outline";

type ComboBoxProps = {
    values: string[];
    selected: string;
    disabled?: boolean;
    placeholder?: string;
    validate?: (value: string) => boolean;
    onChange?: (value: string) => void;
};

export function ComboBox(props: ComboBoxProps) {
    const { values, selected, placeholder, validate: _validate, onChange } = props;

    const validate = _validate ?? (() => true);

    const [query, setQuery] = useState<string>("")

    const filtered_values = query === "" ? values
        : values.filter((value) => { return value.toLowerCase().includes(query.toLowerCase()) });


    const handleOnChange = (value: string) => {
        setQuery("");
        if (onChange) { onChange(value); }
    }

    const handleOnBlur = () => {
        if (query !== "" && onChange && validate(query)) { onChange(query); }
        setQuery("");
    }

    return <Combobox as="div" value={selected} onChange={handleOnChange} className="w-full">
        <div className="relative">
            <ComboboxInput
                className="block w-full rounded-md shadow-sm  bg-white py-2 pr-12 pl-3 border-0 text-base ring-1 ring-inset  ring-gray-300 placeholder-gray-400 focus-within:ring-2 focus-within:ring-inset focus-within:ring-space_blue-600 sm:text-sm"
                onChange={(event) => setQuery(event.target.value)}
                placeholder={placeholder}
                onBlur={handleOnBlur}
            />

            {query.length === 0 && <ComboboxButton className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
                <ChevronUpDownIcon className="w-5 h-5 text-gray-600" aria-hidden="true" />
            </ComboboxButton>}
            {query.length > 0 && validate(query) && <button className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none" onClick={handleOnBlur}>
                <CheckIcon className="w-5 h-5 text-gray-600" aria-hidden="true" />
            </button>}
            {query.length > 0 && !validate(query) && <button className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none" onClick={handleOnBlur}>
                <XMarkIcon className="w-5 h-5 text-torch_red-600" aria-hidden="true" />
            </button>}

            {filtered_values.length > 0 && (
                <ComboboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
                    {filtered_values.map((value, idx) => (
                        <ComboboxOption
                            key={idx}
                            value={value}
                            className="group relative cursor-default py-2 pr-9 pl-3 text-gray-900 select-none data-[focus]:bg-space_blue-600 data-[focus]:text-white data-[focus]:outline-none"
                        >
                            <span className="block truncate group-data-[selected]:font-semibold">{value}</span>

                            <span className="absolute inset-y-0 right-0 hidden items-center pr-4 text-space_blue-600 group-data-[focus]:text-white group-data-[selected]:flex">
                                <CheckIcon className="w-5 h-5" aria-hidden="true" />
                            </span>
                        </ComboboxOption>
                    ))}
                </ComboboxOptions>
            )}
        </div>
    </Combobox>;
}
